import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Ola · Historias de marca.
 *
 * Abre las historias en un dialog modal y las pasa una por una:
 * - El reel dura lo que dura el video (con sonido, porque se abrió con un toque; si el navegador
 *   no lo permite, sin sonido). Cada foto dura `IMAGE_DURATION`. La última, la invitación a ver la
 *   marca, se queda hasta que la persona la cierre o vuelva a empezar.
 * - Toque a la derecha: siguiente (en la última, cierra). Toque a la izquierda: anterior.
 *   Mantener apretado: pausa mientras dure. Deslizar hacia abajo: cierra.
 * - Teclado: flechas para pasar, espacio para pausar y Escape para cerrar (el del dialog).
 * - Al cerrar, guarda que la marca ya se vio (`localStorage`) y el anillo pasa a gris.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} viewer
 * @property {HTMLElement} player
 * @property {HTMLElement[]} slides - Una por historia, con `data-type` video, image o end.
 * @property {HTMLElement[]} fills - Relleno de cada barra de avance.
 * @property {HTMLElement} status - Anuncia «Historia 2 de 5».
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLButtonElement} [soundButton]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
export class OlaHistorias extends Component {
  requiredRefs = ['viewer', 'player', 'slides', 'fills', 'status'];

  /** Duración de cada foto. */
  static IMAGE_DURATION = 6000;

  /** Tiempo apretando para que cuente como pausa y no como toque. */
  static HOLD_DELAY = 220;

  /** Distancia hacia abajo que cierra las historias. */
  static SWIPE_CLOSE = 90;

  #index = 0;
  #paused = false;
  #held = false;
  #muted = false;
  #elapsed = 0;
  #lastTime = 0;
  #frame = 0;
  /** Ignora el clic que llega después de mantener apretado o deslizar. */
  #swallowClick = false;
  /** @type {number | undefined} */
  #holdTimer;
  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#stop();
    unlockScroll(this);
  }

  /**
   * Abre las historias en la que se tocó (`data-index`).
   * @param {Event} event
   */
  open(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest('[data-index]') : null;
    const index = Number(button instanceof HTMLElement ? button.dataset.index : 0) || 0;
    const { viewer } = this.refs;

    if (!viewer.open) {
      viewer.showModal();
      lockScroll(this);
    }
    this.#paused = false;
    this.#muted = false;
    this.#go(index);
    this.refs.closeButton?.focus();
  }

  /** Cierra las historias. */
  close() {
    this.refs.viewer.close();
  }

  /** Siguiente historia; en la última, cierra. */
  next() {
    if (this.#consumeSwallow()) return;
    if (this.#index >= this.refs.slides.length - 1) {
      this.close();
      return;
    }
    this.#go(this.#index + 1);
  }

  /** Historia anterior; en la primera, vuelve a empezarla. */
  prev() {
    if (this.#consumeSwallow()) return;
    this.#go(Math.max(0, this.#index - 1));
  }

  /** Vuelve a la primera historia. */
  replay() {
    this.#go(0);
  }

  /** Pausa o sigue. */
  togglePlay() {
    this.#paused = !this.#paused;
    this.#applyPlayback();
  }

  /** Activa o quita el sonido del reel. */
  toggleSound() {
    this.#muted = !this.#muted;
    const video = this.#currentVideo();
    if (video) video.muted = this.#muted;
    if (!this.#muted && this.#paused) {
      this.#paused = false;
      this.#applyPlayback();
    }
    this.#renderButtons();
  }

  #setup() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { viewer, player } = this.refs;

    this.toggleAttribute('data-seen', this.#wasSeen());

    viewer.addEventListener(
      'close',
      () => {
        this.#stop();
        unlockScroll(this);
        this.#markSeen();
      },
      { signal }
    );

    viewer.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prev();
        } else if (event.key === ' ' && !(event.target instanceof HTMLButtonElement || event.target instanceof HTMLAnchorElement)) {
          event.preventDefault();
          this.togglePlay();
        }
      },
      { signal }
    );

    // Tocar el fondo, fuera del reproductor, también cierra.
    viewer.addEventListener(
      'click',
      (event) => {
        if (event.target === viewer || (event.target instanceof HTMLElement && event.target.classList.contains('ola-historias__stage'))) {
          this.close();
        }
      },
      { signal }
    );

    player.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal });
    player.addEventListener('pointerup', (event) => this.#onPointerUp(event), { signal });
    player.addEventListener('pointercancel', () => this.#onPointerUp(null), { signal });

    for (const slide of this.refs.slides) {
      const video = slide.querySelector('video');
      if (!video) continue;
      video.loop = false;
      video.playsInline = true;
      video.addEventListener(
        'ended',
        () => {
          if (this.refs.slides[this.#index] === slide) this.next();
        },
        { signal }
      );
      video.addEventListener('play', () => this.#renderButtons(), { signal });
      video.addEventListener('pause', () => this.#renderButtons(), { signal });
    }
  }

  /** @param {number} index */
  #go(index) {
    const { slides, fills, status } = this.refs;
    const previous = this.#currentVideo();
    if (previous) previous.pause();

    this.#index = Math.min(Math.max(index, 0), slides.length - 1);
    this.#elapsed = 0;
    this.#lastTime = performance.now();

    slides.forEach((slide, position) => {
      slide.hidden = position !== this.#index;
    });
    fills.forEach((fill, position) => {
      fill.style.transform = `scaleX(${position < this.#index ? 1 : 0})`;
    });

    const video = this.#currentVideo();
    if (video) {
      video.currentTime = 0;
      video.muted = this.#muted;
    }

    // La foto siguiente empieza a cargar ya.
    const nextImage = slides[this.#index + 1]?.querySelector('img');
    if (nextImage) nextImage.loading = 'eager';

    const template = status.dataset.template ?? '';
    status.textContent = template.replace('[current]', String(this.#index + 1)).replace('[total]', String(slides.length));

    this.#applyPlayback();
    this.#loop();
  }

  #applyPlayback() {
    const video = this.#currentVideo();
    if (video) {
      if (this.#paused || this.#held) {
        video.pause();
      } else {
        video.play().catch(() => {
          // Sin permiso para sonido: sigue sin sonido.
          this.#muted = true;
          video.muted = true;
          video.play().catch(() => {});
          this.#renderButtons();
        });
      }
    }
    this.#lastTime = performance.now();
    this.#renderButtons();
  }

  #loop() {
    window.cancelAnimationFrame(this.#frame);
    const tick = (/** @type {number} */ now) => {
      if (!this.refs.viewer.open) return;
      const slide = this.refs.slides[this.#index];
      const fill = this.refs.fills[this.#index];
      let progress = 1;

      if (slide?.dataset.type === 'video') {
        const video = this.#currentVideo();
        progress = video && video.duration ? video.currentTime / video.duration : 0;
      } else if (slide?.dataset.type === 'image') {
        if (!this.#paused && !this.#held) this.#elapsed += now - this.#lastTime;
        progress = this.#elapsed / OlaHistorias.IMAGE_DURATION;
        if (progress >= 1) {
          this.#lastTime = now;
          this.next();
          return;
        }
      }

      this.#lastTime = now;
      if (fill) fill.style.transform = `scaleX(${Math.min(progress, 1)})`;
      this.#frame = window.requestAnimationFrame(tick);
    };
    this.#frame = window.requestAnimationFrame(tick);
  }

  #stop() {
    window.cancelAnimationFrame(this.#frame);
    window.clearTimeout(this.#holdTimer);
    for (const video of this.querySelectorAll('.ola-historias__viewer video')) {
      if (video instanceof HTMLVideoElement) video.pause();
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown(event) {
    if (event.target instanceof Element && event.target.closest('a, .ola-reel-controls, .ola-historias__close, .ola-historias__end')) return;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
    window.clearTimeout(this.#holdTimer);
    this.#holdTimer = window.setTimeout(() => {
      this.#held = true;
      this.#applyPlayback();
    }, OlaHistorias.HOLD_DELAY);
  }

  /** @param {PointerEvent | null} event */
  #onPointerUp(event) {
    window.clearTimeout(this.#holdTimer);
    const start = this.#pointerStart;
    this.#pointerStart = null;

    if (event && start) {
      const dy = event.clientY - start.y;
      const dx = Math.abs(event.clientX - start.x);
      if (dy > OlaHistorias.SWIPE_CLOSE && dx < dy) {
        this.#swallowClick = true;
        this.close();
        return;
      }
    }

    if (this.#held) {
      this.#held = false;
      this.#swallowClick = true;
      this.#applyPlayback();
    }
  }

  #consumeSwallow() {
    if (!this.#swallowClick) return false;
    this.#swallowClick = false;
    return true;
  }

  /** @returns {HTMLVideoElement | null} */
  #currentVideo() {
    const video = this.refs.slides[this.#index]?.querySelector('video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  /** Sonido solo en el reel; pausa en el reel y las fotos (no en la invitación final). */
  #renderButtons() {
    const { soundButton, pauseButton } = this.refs;
    const isVideo = this.#currentVideo() !== null;
    const isEnd = this.refs.slides[this.#index]?.dataset.type === 'end';
    if (soundButton) {
      soundButton.hidden = !isVideo;
      setButton(soundButton, !this.#muted);
    }
    if (pauseButton) {
      pauseButton.hidden = isEnd;
      setButton(pauseButton, this.#paused);
    }
  }

  #storageKey() {
    return `ola-historias:${this.dataset.brand ?? ''}`;
  }

  #wasSeen() {
    try {
      return window.localStorage.getItem(this.#storageKey()) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    this.setAttribute('data-seen', '');
    try {
      window.localStorage.setItem(this.#storageKey(), '1');
    } catch {
      // Sin almacenamiento: el anillo vuelve a verse con color la próxima vez.
    }
  }
}

/**
 * @param {HTMLButtonElement | undefined} button
 * @param {boolean} active - Con sonido (botón de sonido) o en pausa (botón de pausa).
 */
function setButton(button, active) {
  if (!button) return;
  button.toggleAttribute('data-active', active);
  const label = active ? button.dataset.labelOn : button.dataset.labelOff;
  if (label) button.setAttribute('aria-label', label);
}

if (!customElements.get('ola-historias')) {
  customElements.define('ola-historias', OlaHistorias);
}
